import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";

import Loader from "../Loader";
import MockProgressChart from "./MockProgressChart";
import { getMockPerformance } from "../../actions/resultActions";
import {
  bestGrade,
  getAccuracyBand,
  GRADE_BANDS,
  gradeColor,
  gradeToAxisValue,
  TOPIC_BANDS,
  TOPIC_BAND_ORDER,
  toGrade,
  toJambScore,
} from "../../utils/examScoring";

import "./PerformanceTracker.css";

/**
 * Each exam is tracked differently: JAMB and POST UTME progress as a raw score,
 * while WAEC and BECE are followed one subject at a time as a grade ladder.
 */
const EXAM_TABS = [
  {
    slug: "jamb-utme",
    label: "JAMB UTME",
    metric: "score",
    max: 400,
    passMark: 200,
    accent: "#006b3f",
    perSubject: false,
  },
  {
    slug: "wassce-waec-ssce",
    label: "WAEC (WASSCE)",
    metric: "grade",
    accent: "#2563eb",
    perSubject: true,
  },
  {
    slug: "post-utme",
    label: "POST UTME",
    metric: "score",
    max: 100,
    passMark: 50,
    accent: "#7c3aed",
    perSubject: false,
  },
  {
    slug: "jsce-bece",
    label: "BECE",
    metric: "grade",
    accent: "#0891b2",
    perSubject: true,
  },
];

const BAND_HINTS = {
  weak: "Focus your next study session here",
  average: "Making progress — keep building consistency",
  strong: "Making good progress — keep building",
  "very-strong": "Outstanding — protect and maintain this advantage",
};

const BAND_ACTIONS = {
  weak: "Action needed",
  average: "Keep going",
  strong: "Maintain momentum",
  "very-strong": "Excellent",
};

const NODE_ACCENTS = ["#f59e0b", "#7c3aed", "#2563eb", "#16a34a", "#0e7490", "#be185d"];

function formatAxisLabel(date, total) {
  if (!date) return "";
  return total > 6 ? moment(date).format("MMM 'YY") : moment(date).format("DD MMM YYYY");
}

/** Turns one attempt into the score or grade the student should see. */
function readAttempt(attempt, tab) {
  const percent = Number(attempt.percent) || 0;

  if (tab.metric === "grade") {
    const grade = toGrade(percent);
    return {
      display: grade,
      grade,
      value: gradeToAxisValue(grade),
      color: gradeColor(grade),
      percent,
    };
  }

  const score = tab.max === 400 ? toJambScore(percent) : percent;
  return {
    display: String(score),
    value: score,
    color: tab.accent,
    percent,
  };
}

const PerformanceTracker = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { mpLoading, mockPerformance } = useSelector((state) => state.results);

  const [activeSlug, setActiveSlug] = useState(EXAM_TABS[0].slug);
  const [subjectId, setSubjectId] = useState(null);

  const userId = user && user._id;

  useEffect(() => {
    if (userId) dispatch(getMockPerformance(userId));
  }, [dispatch, userId]);

  const tab = useMemo(
    () => EXAM_TABS.find((entry) => entry.slug === activeSlug) || EXAM_TABS[0],
    [activeSlug]
  );

  const exam = useMemo(() => {
    const exams = (mockPerformance && mockPerformance.exams) || [];
    return exams.find((entry) => entry.examSlug === activeSlug) || null;
  }, [mockPerformance, activeSlug]);

  const allAttempts = useMemo(() => {
    if (!exam) return [];
    return [...exam.attempts].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }, [exam]);

  /** Subjects the student has actually sat for in this exam. */
  const subjects = useMemo(() => {
    const seen = new Map();
    allAttempts.forEach((attempt) => {
      (attempt.subjects || []).forEach((subject) => {
        if (!seen.has(subject._id)) seen.set(subject._id, subject);
      });
    });
    (exam ? exam.diagnostics || [] : []).forEach((subject) => {
      if (!seen.has(subject._id)) {
        seen.set(subject._id, { _id: subject._id, title: subject.title, slug: subject.slug });
      }
    });
    return Array.from(seen.values());
  }, [allAttempts, exam]);

  // Reset the subject picker whenever the exam changes.
  useEffect(() => {
    if (!tab.perSubject) {
      setSubjectId(null);
      return;
    }
    setSubjectId(subjects.length > 0 ? subjects[0]._id : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlug, subjects.length]);

  const activeSubject = useMemo(
    () => subjects.find((subject) => subject._id === subjectId) || null,
    [subjects, subjectId]
  );

  /** WAEC and BECE are read per subject; an attempt can cover several subjects. */
  const attempts = useMemo(() => {
    if (!tab.perSubject || !subjectId) return allAttempts;
    return allAttempts.filter((attempt) =>
      (attempt.subjects || []).some((subject) => subject._id === subjectId)
    );
  }, [allAttempts, tab.perSubject, subjectId]);

  const readings = useMemo(
    () => attempts.map((attempt) => ({ attempt, reading: readAttempt(attempt, tab) })),
    [attempts, tab]
  );

  const summary = useMemo(() => {
    if (readings.length === 0) return null;

    const latest = readings[readings.length - 1].reading;
    const previous =
      readings.length > 1 ? readings[readings.length - 2].reading : null;

    if (tab.metric === "grade") {
      const best = bestGrade(readings.map((entry) => entry.reading.grade));
      return {
        latestLabel: latest.grade,
        bestLabel: best,
        thirdValue: String(allAttempts.length),
        thirdLabel: "Total Attempts",
        fourthValue: String(readings.length),
        fourthLabel: "Attempts",
        trend:
          previous && previous.grade !== latest.grade
            ? {
                improved: gradeToAxisValue(latest.grade) > gradeToAxisValue(previous.grade),
                label: `${
                  gradeToAxisValue(latest.grade) > gradeToAxisValue(previous.grade)
                    ? "Improved"
                    : "Dropped"
                } from ${previous.grade} to ${latest.grade}`,
              }
            : null,
      };
    }

    const scores = readings.map((entry) => entry.reading.value);
    const total = scores.reduce((sum, score) => sum + score, 0);
    const delta = previous ? latest.value - previous.value : 0;

    return {
      latestLabel: String(latest.value),
      bestLabel: String(Math.max(...scores)),
      thirdValue: String(Math.round(total / scores.length)),
      thirdLabel: "Average Score",
      fourthValue: String(readings.length),
      fourthLabel: "Attempts",
      trend:
        previous && delta !== 0
          ? {
              improved: delta > 0,
              label: `${delta > 0 ? "+" : ""}${delta} from last`,
            }
          : null,
    };
  }, [readings, tab, allAttempts.length]);

  const chartPoints = useMemo(
    () =>
      readings.map(({ attempt, reading }) => ({
        label: formatAxisLabel(attempt.date, readings.length),
        value: reading.value,
        color: reading.color,
        tooltipTitle: moment(attempt.date).format("DD MMM YYYY"),
        tooltipLabel:
          tab.metric === "grade"
            ? `${reading.grade} — ${reading.percent}% (${attempt.correctCount}/${attempt.questionCount})`
            : `${reading.value}${tab.max === 400 ? " / 400" : "%"} — ${
                attempt.correctCount
              }/${attempt.questionCount} correct`,
      })),
    [readings, tab]
  );

  /**
   * JAMB and POST UTME cover several subjects at once, so subjects are the top
   * level of the diagnosis. Once a single subject is being tracked, its topics
   * take that place and subtopics sit inside them.
   */
  const diagnosisNodes = useMemo(() => {
    const diagnostics = (exam && exam.diagnostics) || [];

    const source = tab.perSubject
      ? (diagnostics.find((subject) => subject._id === subjectId) || { topics: [] }).topics.map(
          (topic) => ({
            id: topic._id,
            title: topic.title,
            correct: topic.correct,
            incorrect: topic.incorrect,
            children: topic.subtopics || [],
          })
        )
      : diagnostics.map((subject) => ({
          id: subject._id,
          title: subject.title,
          correct: subject.correct,
          incorrect: subject.incorrect,
          children: subject.topics || [],
        }));

    return source
      .map((node, index) => ({
        ...node,
        accent: NODE_ACCENTS[index % NODE_ACCENTS.length],
        band: getAccuracyBand(node.correct, node.incorrect),
      }))
      .sort((a, b) => a.band.percent - b.band.percent);
  }, [exam, tab.perSubject, subjectId]);

  const bandGroups = useMemo(
    () =>
      TOPIC_BAND_ORDER.map((bandId) => ({
        id: bandId,
        label: TOPIC_BANDS.find((band) => band.id === bandId).label,
        hint: BAND_HINTS[bandId],
        action: BAND_ACTIONS[bandId],
        nodes: diagnosisNodes.filter((node) => node.band.id === bandId),
      })).filter((group) => group.nodes.length > 0),
    [diagnosisNodes]
  );

  if (mpLoading !== false && !mockPerformance) return <Loader />;

  const chartTitle = tab.perSubject
    ? `${activeSubject ? activeSubject.title : "Subject"} — Grade Progression`
    : `${tab.label} Performance`;

  const chartSubtitle = tab.perSubject
    ? `${tab.label} · Grade on Y-axis, date of attempt on X-axis`
    : `Track your ${tab.label} mock score progression across all attempts.`;

  return (
    <Fragment>
      <div className="tracker">
        <div className="tracker__head">
          <h1 className="tracker__title">Mock Performance Tracker</h1>
          <p className="tracker__subtitle">
            Visualise your score or grade progression across all mock attempts
          </p>
        </div>

        <div className="tracker__tabs">
          {EXAM_TABS.map((entry) => (
            <button
              key={entry.slug}
              type="button"
              className={`tracker__tab${entry.slug === activeSlug ? " is-active" : ""}`}
              style={
                entry.slug === activeSlug ? { background: entry.accent } : undefined
              }
              onClick={() => setActiveSlug(entry.slug)}
            >
              {entry.label}
            </button>
          ))}
        </div>

        {tab.perSubject && subjects.length > 0 && (
          <div className="tracker__card tracker__picker">
            <p className="tracker__picker-head">
              <i className="fas fa-book" /> Select Subject
              <span>— view grade history for a single subject</span>
            </p>
            <div className="tracker__pills">
              {subjects.map((subject) => (
                <button
                  key={subject._id}
                  type="button"
                  className={`tracker__pill${
                    subject._id === subjectId ? " is-active" : ""
                  }`}
                  style={
                    subject._id === subjectId ? { background: tab.accent } : undefined
                  }
                  onClick={() => setSubjectId(subject._id)}
                >
                  {subject.title}
                </button>
              ))}
            </div>
          </div>
        )}

        {!summary ? (
          <div className="tracker__card tracker__empty">
            <span className="tracker__empty-icon">
              <i className="fas fa-chart-line" />
            </span>
            <h2>No {tab.label} mock attempts yet</h2>
            <p>
              {tab.perSubject && subjects.length > 0
                ? "You have not sat a mock in this subject yet. Pick another subject above, or take a mock to start building this chart."
                : "Once you complete a mock for this exam, your score progression and topic diagnosis will appear here."}
            </p>
            <Link className="tracker__cta" to="/mode-of-assessment">
              Take a mock exam <i className="fas fa-angle-right" />
            </Link>
          </div>
        ) : (
          <Fragment>
            <div className="tracker__stats">
              <div className="tracker__stat">
                <span className="tracker__stat-icon is-green">
                  <i className="fas fa-heartbeat" />
                </span>
                <div>
                  <p className="tracker__stat-value">{summary.latestLabel}</p>
                  <span className="tracker__stat-label">Latest</span>
                </div>
              </div>
              <div className="tracker__stat">
                <span className="tracker__stat-icon is-emerald">
                  <i className="fas fa-award" />
                </span>
                <div>
                  <p className="tracker__stat-value">{summary.bestLabel}</p>
                  <span className="tracker__stat-label">Best</span>
                </div>
              </div>
              <div className="tracker__stat">
                <span className="tracker__stat-icon is-amber">
                  <i className="fas fa-bullseye" />
                </span>
                <div>
                  <p className="tracker__stat-value">{summary.thirdValue}</p>
                  <span className="tracker__stat-label">{summary.thirdLabel}</span>
                </div>
              </div>
              <div className="tracker__stat">
                <span className="tracker__stat-icon is-blue">
                  <i className="fas fa-calendar-alt" />
                </span>
                <div>
                  <p className="tracker__stat-value">{summary.fourthValue}</p>
                  <span className="tracker__stat-label">{summary.fourthLabel}</span>
                </div>
              </div>
            </div>

            <div className="tracker__card">
              <div className="tracker__card-head">
                <div>
                  <h2 className="tracker__card-title">{chartTitle}</h2>
                  <p className="tracker__card-subtitle">{chartSubtitle}</p>
                </div>
                {summary.trend && (
                  <span
                    className={`tracker__trend${
                      summary.trend.improved ? " is-up" : " is-down"
                    }`}
                  >
                    <i
                      className={`fas fa-long-arrow-alt-${
                        summary.trend.improved ? "up" : "down"
                      }`}
                    />
                    {summary.trend.label}
                  </span>
                )}
              </div>

              <MockProgressChart
                mode={tab.metric}
                points={chartPoints}
                accent={tab.accent}
                max={tab.max}
                passMark={tab.metric === "score" ? tab.passMark : null}
                passLabel={
                  tab.metric === "score" ? `Recommended Pass ${tab.passMark}` : ""
                }
              />

              {tab.metric === "grade" && (
                <div className="tracker__legend">
                  <span className="tracker__legend-label">Grade Bands</span>
                  {GRADE_BANDS.map((band) => (
                    <span key={band.id} className="tracker__legend-item">
                      <i style={{ background: band.color }} />
                      {band.label}
                    </span>
                  ))}
                </div>
              )}

              <div className="tracker__history">
                <p className="tracker__history-label">Attempt History</p>
                <div className="tracker__history-strip">
                  {readings.map(({ attempt, reading }, index) => (
                    <Link
                      key={attempt.quizId}
                      className={`tracker__attempt${
                        index === readings.length - 1 ? " is-latest" : ""
                      }`}
                      to={`/practice-results/${attempt.quizId}`}
                    >
                      <span
                        className="tracker__attempt-no"
                        style={{ background: tab.accent }}
                      >
                        {index + 1}
                      </span>
                      <span className="tracker__attempt-meta">
                        <b style={{ color: reading.color }}>{reading.display}</b>
                        <em>{moment(attempt.date).format("DD MMM YYYY")}</em>
                      </span>
                      {index === readings.length - 1 && (
                        <span className="tracker__attempt-tag">Latest</span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <div className="tracker__card">
              <div className="tracker__card-head">
                <div>
                  <h2 className="tracker__card-title">Topic &amp; Subtopic Diagnosis</h2>
                  <p className="tracker__card-subtitle">
                    Prioritised — weaknesses first so you know exactly where to study next
                  </p>
                </div>
                <div className="tracker__bands">
                  {TOPIC_BAND_ORDER.map((bandId) => {
                    const band = TOPIC_BANDS.find((entry) => entry.id === bandId);
                    return (
                      <span key={bandId} className="tracker__bands-item">
                        <i style={{ background: band.color }} />
                        {band.label}
                      </span>
                    );
                  })}
                </div>
              </div>

              {bandGroups.length === 0 ? (
                <p className="tracker__note">
                  Topic diagnosis appears once your answered questions have been tagged
                  to topics. Complete another mock to build this view.
                </p>
              ) : (
                bandGroups.map((group) => (
                  <div key={group.id} className="tracker__group">
                    <p className={`tracker__group-head is-${group.id}`}>
                      <span className="tracker__dot" /> {group.label}
                      <em>— {group.hint}</em>
                      <span className="tracker__group-count">
                        {group.nodes.length}{" "}
                        {group.nodes.length === 1 ? "topic" : "topics"}
                      </span>
                    </p>

                    <div className="tracker__nodes">
                      {group.nodes.map((node) => (
                        <div
                          key={node.id}
                          className={`tracker__node tracker__node--${group.id}`}
                        >
                          <div className="tracker__node-head">
                            <span
                              className="tracker__dot"
                              style={{ background: node.accent }}
                            />
                            <h3>{node.title}</h3>
                            <span className={`tracker__node-tag is-${group.id}`}>
                              {group.action}
                            </span>
                          </div>

                          <p className="tracker__node-label">
                            {tab.perSubject ? "Subtopics covered" : "Topics covered"}
                          </p>
                          <ul className="tracker__children">
                            {node.children.length === 0 ? (
                              <li>
                                <span>Not yet broken down</span>
                                <span className="tracker__deltas">
                                  <em className="is-correct">+{node.correct} correct</em>
                                  <em className="is-incorrect">-{node.incorrect} incorrect</em>
                                </span>
                              </li>
                            ) : (
                              node.children.map((child) => {
                                const childBand = getAccuracyBand(
                                  child.correct,
                                  child.incorrect
                                );
                                return (
                                  <li key={child._id}>
                                    <span>
                                      <i style={{ background: childBand.color }} />
                                      {child.title}
                                    </span>
                                    <span className="tracker__deltas">
                                      <em className="is-correct">+{child.correct} correct</em>
                                      <em className="is-incorrect">-{child.incorrect} incorrect</em>
                                    </span>
                                  </li>
                                );
                              })
                            )}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};

export default PerformanceTracker;
